// ── Welle S8: Sitzung — Token, „Angemeldet bleiben“, Abmelden räumt auf ─────────────
// Mit „Angemeldet bleiben“ liegt das Token im localStorage (wie bisher, 24 h, Erneuerung).
// Ohne Haken liegt es im sessionStorage (endet mit dem Browser/Tab) und gilt auf dem Server nur
// 30 Minuten; bei Benutzung wird es erneuert (SitzungWaechter). Tokens von vor S8 im
// localStorage zählen als „angemeldet bleiben“ — niemand wird mitten im Test abgemeldet.
// API_BASE wie in lib/api.ts (hier direkt, damit api.ts holeToken ohne Zirkel nutzen kann)
const API_BASE = process.env.REACT_APP_API_URL ?? '';

export const JWT_KEY = 'lehrerdesk_jwt';
export const KURZ_MINUTEN = 30;
/** Gerätebezogene Bequemlichkeiten ohne Schülerdaten: bleiben beim Abmelden */
export const GERAET_BEHALTEN = ['ld-blatt-abschnitte', 'ld-konferenz-spalten', 'ld-install-dismissed', 'ios-banner-dismissed', 'ld-zeitraster-hinweis-zu'];

export function holeToken(): string | null {
  try { return sessionStorage.getItem(JWT_KEY) ?? localStorage.getItem(JWT_KEY); } catch { return null; }
}
/** Neues Token nach der Anmeldung: Speicherort nach „Angemeldet bleiben“ */
export function setzeToken(token: string, angemeldetBleiben: boolean) {
  try {
    if (angemeldetBleiben) { localStorage.setItem(JWT_KEY, token); sessionStorage.removeItem(JWT_KEY); }
    else { sessionStorage.setItem(JWT_KEY, token); localStorage.removeItem(JWT_KEY); }
  } catch { /* Speicher gesperrt — dann bleibt nur die laufende Seite angemeldet */ }
}
/** Erneuertes Token an denselben Ort wie das alte */
export function ersetzeToken(token: string) {
  try {
    if (sessionStorage.getItem(JWT_KEY)) sessionStorage.setItem(JWT_KEY, token);
    else localStorage.setItem(JWT_KEY, token);
  } catch { /* egal */ }
}
/** Claims des Tokens (ohne Prüfung — nur zur Anzeige und Planung im Browser) */
export function tokenDaten(token = holeToken()): { exp?: number; iat?: number; kurz?: boolean; is_admin?: boolean } | null {
  try { return token ? JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) : null; } catch { return null; }
}
export const istKurzeSitzung = () => !!tokenDaten()?.kurz;
/** Installierte App am Handy (Homescreen): dort ist „Angemeldet bleiben“ vorausgewählt */
export const istInstallierteApp = () => {
  try { return (window.navigator as any).standalone === true || window.matchMedia('(display-mode: standalone)').matches; } catch { return false; }
};

const ohneAuthFetch = () => ((window as any)._origFetch as typeof fetch | undefined) ?? fetch;

/**
 * Löscht im Browser alle App-Daten der Lehrkraft: Push-Anmeldung dieses Geräts, Speicher-
 * Schlüssel der App (außer GERAET_BEHALTEN), sessionStorage, IndexedDB und Caches mit API-
 * Antworten. Danach ist im Browser nichts mehr von ihr übrig.
 */
export async function raeumeAuf() {
  const token = holeToken();
  try {
    const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) {
      if (token) await ohneAuthFetch()(`${API_BASE}/api/push/unsubscribe`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      }).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  } catch { /* Push nicht verfügbar */ }
  try { for (const k of Object.keys(localStorage)) if (!GERAET_BEHALTEN.includes(k)) localStorage.removeItem(k); } catch { /* egal */ }
  try { sessionStorage.clear(); } catch { /* egal */ }
  try {
    const liste = typeof indexedDB.databases === 'function' ? await indexedDB.databases() : [];
    const namen = new Set<string>(['lehrerdesk-diktat-queue', ...liste.map(d => d.name).filter((n): n is string => !!n)]);
    await Promise.all(Array.from(namen).map(n => new Promise<void>(fertig => {
      const r = indexedDB.deleteDatabase(n); r.onsuccess = r.onerror = r.onblocked = () => fertig();
    })));
  } catch { /* egal */ }
  try { if ('caches' in window) for (const k of await caches.keys()) if (/api/i.test(k)) await caches.delete(k); } catch { /* egal */ }
}

/** Abmelden (von Hand oder automatisch): aufräumen und neu starten — Zurück führt nicht in alte Ansichten */
export async function abmeldenUndAufraeumen() {
  await raeumeAuf();
  window.location.replace('/');
}

/** Erneuert das Token (kurze Sitzung: nur solange es gilt — der Server prüft das selbst) */
export async function erneuereToken(): Promise<boolean> {
  const token = holeToken();
  if (!token) return false;
  try {
    const r = await ohneAuthFetch()(`${API_BASE}/api/auth/refresh`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return false;
    const { token: neu } = await r.json();
    if (!neu) return false;
    ersetzeToken(neu);
    return true;
  } catch { return false; }
}
